/** @jsxImportSource @emotion/react */
import { DATADOG_ACTIONS } from '@app/app-constants'
import { roundForDisplay } from '@app/components/Molecules/Charts/utils/utils'
import {
  MoreMenu,
  MoreMenuButton,
  MoreMenuOpenState,
} from '@app/components/Molecules/MapView/RiskMapView/MoreMenu/MoreMenu'
import { generateVulnerabilityPalette } from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import { RegionFilter } from '@app/components/Molecules/RegionFilter'
import { toast } from '@app/components/ui/Toast/toast'
import apiClient from '@app/config/axios.config'
import { useFetchLegends } from '@app/hooks/useFetchLegends'
import { canExportAtom, userAtom } from '@app/hooks/useUserProfile'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { drawAreasAtom, regionFiltersAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import {
  riskMapInstanceAtom,
  riskMapInteractiveAtom,
  riskMapIsFullScreenAtom,
  selectedMarkerAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import {
  prioritisationDrawAreasAtom,
  prioritisationMapInstanceAtom,
} from '@app/state/prioritisationMapAtoms'
import { propertyMapInstanceAtom } from '@app/state/propertyMapAtoms'
import { UpdateDrawAreasProps } from '@app/types/mapTypes'
import { logDataDogAction } from '@app/utils/logger'
import { pollForReport } from '@app/utils/pollForReport'
import {
  Filter,
  Fullscreen,
  FullscreenExit,
  HighlightAlt,
  Print,
  Straighten,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import * as turf from '@turf/turf'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  blobToDataUrl,
  exportMapToClipboard,
  preparePageDuplicateMapAndExportImageBlob,
} from './ExportMap/mapExporter'
import { MapToolsPortal } from './MapToolsPortal'
import { MapToolsContainer } from './MapToolsPortal.styles'
import MeasuringTool, { Measurement, MeasuringToolOutput } from './MeasuringTool'
import SelectionTool, { SelectionArea, SelectionToolOutput } from './SelectionTool'

const MapTools = ({
  mapOverlayRef,
  format,
  size = 'medium',
}: {
  mapOverlayRef: RefObject<HTMLDivElement>
  format: 'prioritisation' | 'risk' | 'property'
  size?: 'small' | 'medium' | 'large'
}) => {
  const clientName = localStorage.getItem('client_name')
  const riskDrawAreas = useAtomValue(drawAreasAtom)
  const riskMap = useAtomValue(riskMapInstanceAtom)
  const propertyMap = useAtomValue(propertyMapInstanceAtom)
  const prioritisationMap = useAtomValue(prioritisationMapInstanceAtom)
  const setDrawAreas = useSetAtom(drawAreasAtom)
  const setInteractive = useSetAtom(riskMapInteractiveAtom)
  const regionFilters = useAtomValue(regionFiltersAtom)
  const canExport = useAtomValue(canExportAtom)
  const { data: user } = useAtomValue(userAtom)
  const setPrioritisationDrawAreas = useSetAtom(prioritisationDrawAreasAtom)

  const [isFullScreen, setIsFullScreen] = useAtom(riskMapIsFullScreenAtom)
  const { data: legendsData = {} } = useFetchLegends()
  const { layers } = useRiskLayersState()
  const prioritisationDrawAreas = useAtomValue(prioritisationDrawAreasAtom)
  const drawAreas = format == 'risk' ? riskDrawAreas : prioritisationDrawAreas
  const tab = 'Risk'
  const vulnerabilityPalette = generateVulnerabilityPalette(legendsData, 'vulnerability')

  const map = format === 'risk' ? riskMap : format === 'property' ? propertyMap : prioritisationMap

  const handleUpdateDrawAreas = useCallback(
    ({ drawAreas }: UpdateDrawAreasProps) => {
      if (format === 'risk') {
        setDrawAreas(drawAreas)
        return
      }

      if (format === 'prioritisation') {
        setPrioritisationDrawAreas(drawAreas)
      }
    },
    [format, setDrawAreas, setPrioritisationDrawAreas],
  )

  const [isSelecting, setIsSelecting] = useState(false)
  const [hasSelection, setHasSelection] = useState(false)
  const [isMeasuring, setIsMeasuring] = useState(false)
  const [measurement, setMeasurement] = useState<Measurement>({
    distance: '0 m',
    area: null,
    perimeter: null,
  })
  const [selectionArea, setSelectionArea] = useState<SelectionArea>({
    area: null,
    perimeter: null,
  })
  const { liveMapSelected } = useLiveContext()
  const [selectedMarker] = useAtom(selectedMarkerAtom)
  const expanding = useMemo(() => format == 'risk', [format])

  const deleteSelection = useCallback(() => {
    if (!map) return
    SelectionTool.deleteSelectionArea(map)
  }, [map])

  const handleExportToPdf = useCallback(async () => {
    if (!map) return

    toast({ description: 'Exporting to PDF', type: 'info' })

    let mapImageDataUrl = ''

    await preparePageDuplicateMapAndExportImageBlob(
      map,
      769,
      490,
      async (imageBlob) => {
        mapImageDataUrl = await blobToDataUrl(imageBlob)
      },
      vulnerabilityPalette,
      selectedMarker,
    )
    const mapImageUploadResponse = await apiClient.post(`/report/mapimage`, {
      dataUrl: mapImageDataUrl,
    })

    if (mapImageUploadResponse.status != 200) throw new Error('Map image failed to upload')

    const mapImageDownloadUrl = mapImageUploadResponse.data as string

    const mapHeightInPixels = map.getCanvas().height
    const mapWidthInPixels = map.getCanvas().width

    const mapWidthInMetres = turf.distance(
      map.unproject([0, mapHeightInPixels / 2.0]).toArray(),
      map.unproject([mapWidthInPixels, mapHeightInPixels / 2.0]).toArray(),
      { units: 'meters' },
    )

    const layerTypes: string[] = []
    const assetLayers = layers.filter((layer) => layer.layerType === 'asset')
    const hazardLayers = layers.filter((layer) => layer.layerType === 'hazard')
    const liveLayers = layers.filter((layer) => layer.layerType === 'live')
    assetLayers.forEach((layer) => layerTypes.push(layer.type))
    hazardLayers.forEach((layer) => layerTypes.push(layer.assetTag))
    liveLayers.forEach((layer) => layerTypes.push('live_' + layer.display_name))

    const report_id = `${user?.user_uuid}-${Date.now()}`

    try {
      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'print-map',
        format: 'pdf',
      })

      await apiClient.post(`/report/export/${clientName}/print-map-to-pdf/${report_id}`, {
        mapImage: {
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          mapWidthInMetres,
          url: mapImageDownloadUrl,
        },
        layerTypes,
        format,
        regions: regionFilters?.map((region) => region.region_name),
      })

      pollForReport(report_id, 'ResilienceExplorer.pdf')
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }, [
    map,
    vulnerabilityPalette,
    selectedMarker,
    layers,
    user?.user_uuid,
    clientName,
    format,
    regionFilters,
  ])

  const handleExportToClipboard = useCallback(async () => {
    if (!map) return

    // To avoid flashing toasts on the screen, only show info toast if it takes long, otherwise success info is enough.
    const copyingToastTimer = setTimeout(() => {
      toast({
        description: 'Copying map to clipboard...',
        type: 'info',
      })
    }, 750)

    try {
      await exportMapToClipboard(map, vulnerabilityPalette, selectedMarker)

      clearTimeout(copyingToastTimer)

      toast({
        description: 'Copied to clipboard',
        type: 'success',
      })
    } catch (error) {
      clearTimeout(copyingToastTimer)

      console.error('Failed to copy map to clipboard', error)

      toast({
        description: 'Failed to copy to clipboard',
        type: 'error',
      })
    }
  }, [map, selectedMarker, vulnerabilityPalette])

  const buttons: MoreMenuButton[] = useMemo(() => {
    // Safari browsers require daft async handling to be able to copy to the clipboard
    // It's possible to get it going but would require a significant rebuild of
    // already quite fragile code - if you're keen to get it to work then please be my guest - KP
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    // Doesn't work on Firefox - no appetite to fix.
    const isFirefox = /mozilla.*?gecko.*?firefox/i.test(navigator.userAgent)
    const copyButton: MoreMenuButton[] =
      isSafari || isFirefox
        ? []
        : [
            {
              title: 'Copy map as image to clipboard',
              handleClick: handleExportToClipboard,
              icon: <Filter className="tutorial-copy-map" />,
              isHidden: !canExport,
            },
          ]

    const selectElementsButton: MoreMenuButton[] = [
      {
        title: 'Select elements',
        handleClick: () => {
          if (hasSelection) deleteSelection()
          setIsSelecting(true)
          setIsMeasuring(false)
        },
        icon: <HighlightAlt className="tutorial-polygon-draw" />,
        disabled: !(clientName && !isSelecting),
      },
    ]

    const measureButton: MoreMenuButton[] = [
      {
        title: 'Measure distances and area',
        handleClick: () => {
          setIsMeasuring(true)
          setIsSelecting(false)
        },
        icon: <Straighten className="tutorial-measure-tool" />,
        disabled: isSelecting,
      },
    ]

    const fullscreenButton: MoreMenuButton[] = [
      {
        title: isFullScreen ? 'Exit full screen' : 'Full screen',
        handleClick: () => {
          setIsFullScreen(!isFullScreen)
          if (!isFullScreen) {
            document.dispatchEvent(new CustomEvent('EnteringFullScreen'))
          }
          // Toggle fullscreen
          if (document.fullscreenElement) {
            document.exitFullscreen()
          } else {
            document.documentElement.requestFullscreen()
          }
        },
        icon: isFullScreen ? (
          <FullscreenExit className="tutorial-fullscreen-map" />
        ) : (
          <Fullscreen className="tutorial-fullscreen-map" />
        ),
      },
    ]

    const exportToPdfButton = [
      {
        title: `${clientName ? 'Print map to PDF' : 'Login required'}`,
        handleClick: handleExportToPdf,
        icon: <Print className="tutorial-save-map" />,
        disabled: !clientName,
        isHidden: !canExport,
      },
    ]

    if (tab !== 'Risk') {
      return [...exportToPdfButton, ...copyButton, ...fullscreenButton]
    }

    if (format === 'risk')
      return [
        ...(liveMapSelected ? [] : selectElementsButton),
        ...exportToPdfButton,
        ...copyButton,
        ...measureButton,
        ...fullscreenButton,
      ]

    if (format === 'prioritisation') return [...exportToPdfButton, ...copyButton]

    if (format === 'property') return [...copyButton]

    return []
  }, [
    handleExportToClipboard,
    canExport,
    clientName,
    isSelecting,
    isFullScreen,
    handleExportToPdf,
    format,
    liveMapSelected,
    hasSelection,
    deleteSelection,
    setIsFullScreen,
  ])
  const regionsEnabled = useMemo(() => tab === 'Risk' && format === 'risk', [format, tab])

  // Watch for fullscreenchange - this does not trigger if user pressed F11
  useEffect(() => {
    function onFullscreenChange() {
      setIsFullScreen(Boolean(document.fullscreenElement))
    }

    document.addEventListener('fullscreenchange', onFullscreenChange)

    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [setIsFullScreen])

  // Close toolbar when entering fullscreen
  const moreMenuOpenRef = useRef<MoreMenuOpenState>(null)
  useEffect(() => {
    function onEnterFullscreen() {
      moreMenuOpenRef.current?.setOpen(false)
    }

    document.addEventListener('EnteringFullScreen', onEnterFullscreen)

    return () => document.removeEventListener('EnteringFullScreen', onEnterFullscreen)
  }, [])

  // If user leaves page while drawing for example isDrawing is reset to false but
  // this useEffect picks up on this and also reacts to isDrawing and isMeasuring state changes above.
  useEffect(() => {
    setInteractive(!(isSelecting || isMeasuring))
    if (!map || !map.getCanvas()) return

    // So that the mouse pointer gets set correctly first
    // deactivate all tools and then active the selected one
    MeasuringTool.deactivateMeasuringTool(map)
    SelectionTool.deactivateSelectionTool(map)
    if (isMeasuring) MeasuringTool.activateMeasuringTool(map, setMeasurement)
    if (isSelecting)
      SelectionTool.activateSelectionTool(
        map,
        handleUpdateDrawAreas,
        setIsSelecting,
        setHasSelection,
        setSelectionArea,
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelecting, isMeasuring, setInteractive])
  useEffect(() => {
    // This is a bit of a hack.
    // If they navigate away from the Risk Map with an active polygon tool and return
    // there's no way to properly recover the state of the SelectionTool due to it being
    // an awful mix of plain old js and react.
    // When there are any draw areas and the hasSelection flag is not set this means the
    // SelectionTool is in the wrong state so clear all the areas and remove all the
    // SelectionTool layers and source.
    if (map && !isSelecting && !hasSelection && drawAreas.length > 0) {
      if (map.getLayer('selectionToolLineLayer')) {
        map.removeLayer('selectionToolLineLayer')
      }

      if (map.getLayer('selectionToolAreaLayer')) {
        map.removeLayer('selectionToolAreaLayer')
      }

      if (map.getSource('selectionToolSource')) {
        map.removeSource('selectionToolSource')
      }

      setDrawAreas([])
    }
  }, [map, isSelecting, hasSelection, drawAreas, setDrawAreas])
  return (
    <>
      <MoreMenu
        buttons={buttons}
        size={size}
        moreButtonIcon="Spanner"
        showOutline
        defaultOpen
        ref={moreMenuOpenRef}
        disableExpanding={!expanding}
      >
        {regionsEnabled ? <RegionFilter /> : undefined}
      </MoreMenu>
      {(isMeasuring || isSelecting || hasSelection) &&
        mapOverlayRef.current &&
        createPortal(
          <MapToolsPortal>
            <Box css={MapToolsContainer}>
              {(isSelecting || hasSelection) && (
                <SelectionToolOutput
                  handleClose={() => deleteSelection()}
                  selectionArea={selectionArea}
                  isSelecting={isSelecting}
                />
              )}
              {isMeasuring && (
                <MeasuringToolOutput
                  handleClose={() => setIsMeasuring(false)}
                  measurement={measurement}
                />
              )}
            </Box>
          </MapToolsPortal>,
          mapOverlayRef.current,
        )}
    </>
  )
}

export function formatDistance(metres: number): string {
  if (metres < 1000) return `${roundForDisplay(metres, 'm')} m`

  const km = metres / 1000

  return `${roundForDisplay(km, 'km')} km`
}

export function formatArea(squareMetres: number): string {
  const OneMillionHecatares = 1e10
  if (squareMetres < OneMillionHecatares) {
    const hectares = squareMetres / 10000
    return `${roundForDisplay(hectares, 'ha')} ha`
  }

  const sqkm = squareMetres / 1e6
  return `${roundForDisplay(sqkm, 'km²')} km²`
}

export const RiskMapTools = ({ mapOverlayRef }: { mapOverlayRef: RefObject<HTMLDivElement> }) => {
  return <MapTools mapOverlayRef={mapOverlayRef} format="risk" />
}

export const PrioritisationMapTools = ({
  mapOverlayRef,
}: {
  mapOverlayRef: RefObject<HTMLDivElement>
}) => {
  return <MapTools mapOverlayRef={mapOverlayRef} format="prioritisation" />
}

export const PropertyMapTools = ({
  mapOverlayRef,
}: {
  mapOverlayRef: RefObject<HTMLDivElement>
}) => {
  return <MapTools mapOverlayRef={mapOverlayRef} format="property" size="large" />
}
